import { useState, useCallback } from "react";
import { searchApps } from "@/services/appStoreApi";
import { AppStoreSearchResult, AppPlatform } from "@/types/application";
import { toast } from "sonner";

interface SearchParams {
  term: string;
  country: string;
  platform: AppPlatform;
}

const PLATFORM_MAP: Record<AppPlatform, 'iOS' | 'macOS'> = {
  ios: 'iOS', ipados: 'iOS', macos: 'macOS', tvos: 'iOS',
};

export const useItunesSearch = () => {
  const [results, setResults] = useState<AppStoreSearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [lastSearchTerm, setLastSearchTerm] = useState("");

  const search = useCallback(async ({ term, country, platform }: SearchParams) => {
    if (!term.trim()) { setResults([]); setLastSearchTerm(""); return; }
    try {
      setLoading(true); setError(null); setLastSearchTerm(term);
      const data = await searchApps(term.trim(), PLATFORM_MAP[platform] || 'iOS', 25, country);
      const mapped: AppStoreSearchResult[] = (data || []).map((app: any) => ({
        appStoreId: app.trackId || app.id || '',
        name: app.name || app.trackName || '',
        icon: app.artworkUrl60 || app.iconUrl || '',
        iconLarge: app.artworkUrl512 || app.iconUrl || '',
        description: app.description || '',
        version: app.version || '',
        developer: app.sellerName || app.developer || '',
        price: app.price || 0,
        formattedPrice: app.formattedPrice || 'Free',
        bundleId: app.bundleId || '',
        minimumOsVersion: app.minimumOsVersion || '',
        releaseDate: app.releaseDate || '',
        size: app.fileSizeBytes || '0',
        rating: app.averageUserRating || 0,
        ratingCount: app.userRatingCount || 0,
        appStoreUrl: app.trackViewUrl || '',
        category: app.primaryGenreName || '',
        categories: app.genres || [],
        platform: platform,
      }));
      setResults(mapped);
    } catch (err) {
      const e = err instanceof Error ? err : new Error('Search failed');
      setError(e); toast.error(`Search failed: ${e.message}`); setResults([]);
    } finally { setLoading(false); }
  }, []);

  const clearResults = useCallback(() => { setResults([]); setLastSearchTerm(""); setError(null); }, []);
  return { results, loading, error, lastSearchTerm, search, clearResults };
};
